import { create } from 'zustand';

/**
 * Per-device playback preferences.
 *
 * These belong to the browser, not the profile — a learner who watches at 1.5x
 * on their phone has said nothing about their laptop — so they live in
 * localStorage, the same shape as `member.themeMode`. Every access is guarded
 * for both server-side import (the vitest run has no `window`) and private-mode
 * storage refusals.
 */
const STORAGE_KEY = 'member.player';

export type PlayerPrefs = {
  rate: number;
  autoplay: boolean;
  captionsLang: string | null;
  muted: boolean;
  volume: number;
};

const DEFAULTS: PlayerPrefs = {
  rate: 1,
  autoplay: true,
  captionsLang: null,
  muted: false,
  volume: 1,
};

function readStored(): PlayerPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<PlayerPrefs>;
    return {
      rate: typeof parsed.rate === 'number' && parsed.rate > 0 ? parsed.rate : DEFAULTS.rate,
      autoplay: typeof parsed.autoplay === 'boolean' ? parsed.autoplay : DEFAULTS.autoplay,
      captionsLang: typeof parsed.captionsLang === 'string' ? parsed.captionsLang : null,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULTS.muted,
      volume: typeof parsed.volume === 'number' ? parsed.volume : DEFAULTS.volume,
    };
  } catch {
    return DEFAULTS;
  }
}

function writeStored(prefs: PlayerPrefs) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode / storage disabled — the session still honours the choice */
  }
}

type PlayerPrefsState = PlayerPrefs & {
  setRate: (rate: number) => void;
  setAutoplay: (autoplay: boolean) => void;
  setCaptionsLang: (lang: string | null) => void;
  setMuted: (muted: boolean) => void;
  setVolume: (volume: number) => void;
};

export const usePlayerPrefs = create<PlayerPrefsState>((set, get) => {
  const persist = (patch: Partial<PlayerPrefs>) => {
    const { rate, autoplay, captionsLang, muted, volume } = { ...get(), ...patch };
    writeStored({ rate, autoplay, captionsLang, muted, volume });
    set(patch);
  };
  return {
    ...readStored(),
    setRate: (rate) => persist({ rate }),
    setAutoplay: (autoplay) => persist({ autoplay }),
    setCaptionsLang: (captionsLang) => persist({ captionsLang }),
    setMuted: (muted) => persist({ muted }),
    setVolume: (volume) => persist({ volume }),
  };
});

export const PLAYER_PREFS_DEFAULTS = DEFAULTS;
export const PLAYER_PREFS_STORAGE_KEY = STORAGE_KEY;
